import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toolbar } from 'primereact/toolbar';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './PaymentPredictions.scss';

class PaymentPredictions extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            activeIndex: 0,
            forecast: {
                days30: { anticipated: 0, predicted: 0, count: 0 },
                days60: { anticipated: 0, predicted: 0, count: 0 },
                days90: { anticipated: 0, predicted: 0, count: 0 }
            },
            defaults: [],
            defaultsFilter: null
        };
        this.dt = React.createRef();
    }

    componentDidMount() {
        this.fetchPredictions();
        this.setTabFromRoute();
    }

    componentDidUpdate(prevProps) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            this.setTabFromRoute();
        }
    }

    setTabFromRoute = () => {
        const path = this.props.location ? this.props.location.pathname : window.location.pathname;
        if (path.includes('DefaultProbability')) {
            this.setState({ activeIndex: 1 });
        } else {
            this.setState({ activeIndex: 0 });
        }
    }

    fetchPredictions = async () => {
        const academicYear = localStorage.getItem('userAcademicYear');

        // Relaxed check: Only require academicYear. orgId can be inferred by backend if missing.
        if (!academicYear) {
            console.warn('PaymentPredictions: Missing academicYear, skipping fetch');
            return;
        }

        this.setState({ loading: true });

        try {
            // Build URL without orgId - backend will extract from bearer token
            let url = `${baseUrlAdmin}/fee-decision/predictions?academicYear=${academicYear}`;


            const response = await this.service.get(url, true);


            if (response?.status && response?.res?.success) {
                const { forecast, defaults } = response.res.data;
                this.setState({
                    forecast: forecast,
                    defaults: defaults
                });
            } else {
                const errorMsg = response?.res?.message || 'Failed to fetch predictions';
                this.showToast('error', 'Error', errorMsg);
            }
        } catch (error) {
            console.error('Error fetching prediction data:', error);
            this.showToast('error', 'Error', error.message || 'Internal server error');
        } finally {
            this.setState({ loading: false });
        }
    };

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    getChartData = () => {
        const { forecast } = this.state;

        return {
            labels: ['30 Days', '60 Days', '90 Days'],
            datasets: [
                {
                    label: 'Anticipated Inflow (₹)',
                    backgroundColor: '#42A5F5',
                    data: [
                        forecast.days30.anticipated,
                        forecast.days60.anticipated,
                        forecast.days90.anticipated
                    ]
                },
                {
                    label: 'Risk-Adjusted Predicted Inflow (₹)',
                    backgroundColor: '#66BB6A',
                    data: [
                        forecast.days30.predicted,
                        forecast.days60.predicted,
                        forecast.days90.predicted
                    ]
                }
            ]
        };
    };

    getChartOptions = () => {
        return {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    labels: {
                        color: '#495057'
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: '#495057'
                    },
                    grid: {
                        color: '#ebedef'
                    }
                },
                y: {
                    ticks: {
                        color: '#495057'
                    },
                    grid: {
                        color: '#ebedef'
                    }
                }
            }
        };
    };

    formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
    };

    riskScoreBody = (rowData) => {
        const band = rowData.riskBand?.toUpperCase();
        let severity = null;
        let style = null;

        switch (band) {
            case 'CRITICAL': style = { backgroundColor: '#FFA6BC', color: '#BF0031' }; break;
            case 'HIGH': style = { backgroundColor: '#FFEDD5', color: '#C2410C' }; break; // Explicit Dark Orange
            case 'MEDIUM': style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; break; // Explicit Yellow
            case 'LOW': style = { backgroundColor: '#DCFCE7', color: '#268900' }; break;
            default: style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
        }

        return (
            <div className="flex align-items-center gap-2">
                <span className="font-bold">{rowData.riskScore}</span>
                <Tag value={rowData.riskBand} severity={severity} style={style} />
            </div>
        );
    };

    probabilityBody = (rowData) => {
        return <span className="font-bold text-red-500">{rowData.probability}</span>; // Probability of Payment
    };

    amountBody = (rowData) => {
        return this.formatCurrency(rowData.amount);
    }

    dateBody = (rowData) => {
        if (!rowData.dueDate) return '-';
        return new Date(rowData.dueDate).toLocaleDateString('en-GB');
    }

    formatDateFixed = (dateInput) => {
        if (!dateInput) return '';
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) return '';
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    }

    exportCSV = () => {
        const { defaults } = this.state;
        if (!defaults || defaults.length === 0) {
            this.showToast('warn', 'Export', 'No data to export');
            return;
        }

        // Manual CSV Export
        const headers = ['Student Name', 'Adm No', 'Class', 'Next Due Date', 'Total Amount at Risk', 'Pay Probability', 'Risk Score', 'Risk Band'];
        
        const csvRows = [
            headers.join(','),
            ...defaults.map(row => [
                `"${row.studentName || ''}"`,
                `"${row.admissionNo || ''}"`,
                `"${row.className || ''}"`,
                `"${this.formatDateFixed(row.dueDate)}"`,
                row.amount,
                `"${row.probability || ''}"`,
                row.riskScore,
                `"${row.riskBand || ''}"`
            ].join(','))
        ];

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", "payment_forecast_defaults.csv");
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    render() {
        const { loading, forecast, defaults, activeIndex } = this.state;

        // Calculate Cumulative Totals for Cards
        const pred30 = forecast.days30.predicted;
        const anti30 = forecast.days30.anticipated;
        
        const pred60 = pred30 + forecast.days60.predicted;
        const anti60 = anti30 + forecast.days60.anticipated;

        const pred90 = pred60 + forecast.days90.predicted;
        const anti90 = anti60 + forecast.days90.anticipated;

        // Calculate Percentages
        const perc30 = anti30 > 0 ? ((pred30 / anti30) * 100).toFixed(1) : 0;
        const perc60 = anti60 > 0 ? ((pred60 / anti60) * 100).toFixed(1) : 0;
        const perc90 = anti90 > 0 ? ((pred90 / anti90) * 100).toFixed(1) : 0;

        return (
            <div className="payment-predictions-wrapper">
                <Toast ref={this.toast} position="bottom-right" />

                <div className="flex justify-content-between align-items-center mb-3">
                    <h2 className="page-title m-0">Payment Predictions (90-Day Horizon)</h2>
                    <Authorizer permId={PERMISSIONS.FDE_EXPORT_COLLECTION_FORECAST}>
                        <Button
                            label="Export Forecast"
                            icon="pi pi-download"
                            className="p-button-outlined p-button-success"
                            onClick={this.exportCSV}
                            tooltip="Download Student Probabilities (CSV)"
                            tooltipOptions={{ position: 'bottom' }}
                        />
                    </Authorizer>
                </div>

                <div className="grid mb-4">
                    <div className="col-12 md:col-4">
                        <Card className="metric-card bg-blue-50 border-blue-200 h-full">
                            <div className="text-center">
                                <h3 className="text-blue-800 mb-2">30-Day Collection</h3>
                                <h1 className="text-blue-600 m-0">{this.formatCurrency(pred30)}</h1>
                                <p className="text-sm font-bold text-blue-800 mt-2">{perc30}% of Total Due ({this.formatCurrency(anti30)})</p>
                                <p className="text-xs text-gray-600">Next 30 Days Forecast</p>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-4">
                        <Card className="metric-card bg-indigo-50 border-indigo-200 h-full">
                            <div className="text-center">
                                <h3 className="text-indigo-800 mb-2">60-Day Collection</h3>
                                <h1 className="text-indigo-600 m-0">{this.formatCurrency(pred60)}</h1>
                                <p className="text-sm font-bold text-indigo-800 mt-2">{perc60}% of Total Due ({this.formatCurrency(anti60)})</p>
                                <p className="text-xs text-gray-600">Cumulative (Next 60 Days)</p>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-4">
                        <Card className="metric-card bg-purple-50 border-purple-200 h-full">
                            <div className="text-center">
                                <h3 className="text-purple-800 mb-2">90-Day Collection</h3>
                                <h1 className="text-purple-600 m-0">{this.formatCurrency(pred90)}</h1>
                                <p className="text-sm font-bold text-purple-800 mt-2">{perc90}% of Total Due ({this.formatCurrency(anti90)})</p>
                                <p className="text-xs text-gray-600">Cumulative (Next 90 Days)</p>
                            </div>
                        </Card>
                    </div>
                </div>

                <div className="card-custom">
                    <TabView activeIndex={activeIndex} onTabChange={(e) => this.setState({ activeIndex: e.index })}>
                        <TabPanel header="Collection Forecast">
                            <div className="grid">
                                <div className="col-12 lg:col-6">
                                    <div className="chart-container">
                                        <h4 className="chart-title">Inflow vs Forecast (90 Days)</h4>
                                        <Chart type="bar" data={this.getChartData()} options={this.getChartOptions()} style={{ height: '350px' }} />
                                    </div>
                                </div>
                                <div className="col-12 lg:col-6">
                                    <h4 className="mb-3">Forecast Details</h4>
                                    <DataTable value={[
                                        { period: 'Next 30 Days', ...forecast.days30 },
                                        { period: '31-60 Days', ...forecast.days60 },
                                        { period: '61-90 Days', ...forecast.days90 }
                                    ]} responsiveLayout="scroll" className="p-datatable-sm">
                                        <Column field="period" header="Period" />
                                        <Column field="count" header="Installments Due"/>
                                        <Column field="anticipated" header="Anticipated (₹)" body={(r) => this.formatCurrency(r.anticipated)} />
                                        <Column field="predicted" header="Predicted (₹)" body={(r) => this.formatCurrency(r.predicted)}  />
                                    </DataTable>

                                    <div className="mt-4 p-3 bg-gray-50 border-round">
                                        <h5><i className="pi pi-info-circle mr-2" />How it works</h5>
                                        <p className="text-sm line-height-3">
                                            The system analyzes upcoming due dates and applies a <b>payment probability factor</b> based on each student's Risk Score.
                                        </p>
                                        <ul className="text-sm pl-3">
                                            <li>Low Risk students: ~90-100% realization probability</li>
                                            <li>Medium Risk: ~70-80% realization probability</li>
                                            <li>High/Critical Risk: ~10-40% realization probability</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </TabPanel>

                        <TabPanel header="Default Probability List">
                            <div className="col-12">
                                <div className="flex justify-content-between align-items-center mb-3">
                                    <h4 className="m-0">Probable Defaults (High Risk Students with Upcoming Dues)</h4>
                                </div>

                                <DataTable
                                    ref={this.dt}
                                    value={defaults}
                                    paginator
                                    rows={10}
                                    emptyMessage="No high-risk defaults predicted in the next 90 days."
                                    filterDisplay="menu"
                                    rowsPerPageOptions={[10, 25, 50]}
                                >
                                    <Column field="studentName" header="Student Name" sortable />
                                    <Column field="admissionNo" header="Adm No" sortable />
                                    <Column field="className" header="Class" sortable />
                                    <Column field="dueDate" header="Next Due Date" body={this.dateBody} sortable />
                                    <Column field="amount" header="Total Amount at Risk" body={this.amountBody} sortable />
                                    <Column field="probability" header="Pay Probability" body={this.probabilityBody} sortable  />
                                    <Column field="riskScore" header="Risk Level" body={this.riskScoreBody} sortable />
                                </DataTable>
                            </div>
                        </TabPanel>
                    </TabView>
                </div>

                {loading && <LoadingComponent />}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.currentUserPerms ? state.currentUserPerms.userInfo : {}
});

export default connect(mapStateToProps)(withRouter(PaymentPredictions));
